import { Save } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { getInitials } from "../utils/StringTool";
import { useProfile } from "./hooks/useProfile";

const ProfilePage: React.FC = () => {
  const {
    user,
    form,
    handlePickPhoto,
    fileInputRef,
    uploading,
    reloading,
    handleChange,
    handleBlur,
    errors,
    setErrors,
    setForm,
    saving,
    handleSave,
    initialForm,
  } = useProfile();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: { xs: 1, md: 2 },
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          p: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          spacing={3}
          sx={{ textAlign: { xs: "center", sm: "left" } }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Avatar
              src={user?.photoURL || ""}
              alt={form.fullName || "Usuario"}
              sx={{
                width: 90,
                height: 90,
                fontSize: 28,
                fontWeight: 700,
                bgcolor: "secondary.main",
                color: "white",
                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              }}
            >
              {!user?.photoURL && getInitials(form.fullName)}
            </Avatar>

            {/* <Tooltip title="Cambiar foto" arrow>
              <IconButton
                color="primary"
                size="small"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  bgcolor: "background.paper",
                  boxShadow: 2,
                  "&:hover": { bgcolor: "primary.main", color: "white" },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip> */}

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handlePickPhoto}
              style={{ display: "none" }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {form.fullName || "Anónimo"}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {user?.email || user?.phoneNumber || "Sin contacto"}
            </Typography>

            {(uploading || reloading) && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {uploading ? "Subiendo foto..." : "Recargando..."}
              </Typography>
            )}
          </Box>
        </Stack>
      </Card>

      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          background: "linear-gradient(135deg, #fdfdfd 0%, #f5f7fb 100%)",
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              Información del perfil
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Actualiza tus datos personales
            </Typography>
          }
          sx={{ pb: 0 }}
        />

        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Nombre completo"
                value={form.fullName}
                onChange={handleChange("fullName")}
                onBlur={handleBlur("fullName")}
                placeholder="Anónimo"
                inputProps={{ maxLength: 80 }}
                error={!!errors.fullName}
                helperText={
                  errors.fullName ||
                  "Si lo dejas vacío, se guardará como 'Anónimo'."
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de pasaporte"
                value={form.document || ""}
                onChange={handleChange("document")}
                onBlur={handleBlur("document")}
                placeholder="1234567890"
                inputProps={{ maxLength: 20 }}
                error={!!errors.document}
                helperText={
                  errors.document ||
                  "No puedes cambiar tu número de pasaporte. Si es incorrecto, contacta soporte."
                }
                disabled
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <Button
              variant="outlined"
              onClick={() => {
                setForm(initialForm);
                setErrors({});
              }}
              disabled={saving || uploading || reloading}
              sx={{
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Restablecer
            </Button>

            <Button
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={saving || uploading || reloading}
              startIcon={<Save />}
              color="secondary"
              sx={{
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfilePage;
